import { countryCodes } from '@hue/contracts';
import { Combobox, DocumentInput, Input, MaskedInput } from '@hue/design-system';
import { useEffect, useRef, useState } from 'react';
import { companyIdentityApi } from '../lib/api.js';
import type { CompanyContactDetails } from '../lib/api.js';
import { ContactNumbersEditor } from './contact-numbers-editor.js';

export function CompanyContactFields({
  value,
  onChange,
  disabled,
}: {
  value: CompanyContactDetails;
  onChange: (value: CompanyContactDetails) => void;
  disabled: boolean;
}) {
  const latest = useRef({ value, disabled });
  useEffect(() => {
    latest.current = { value, disabled };
  }, [value, disabled]);
  const request = useRef(0);
  const [hint, setHint] = useState('Com o CEP, o endereço vem preenchido.');
  async function lookup(text: string) {
    const version = ++request.current;
    const digits = text.replace(/\D/gu, '');
    setHint('Com o CEP, o endereço vem preenchido.');
    if (digits.length !== 8) return;
    setHint('Buscando o endereço…');
    const currentRequest = () =>
      version === request.current &&
      !latest.current.disabled &&
      latest.current.value.address?.country === 'BR' &&
      latest.current.value.address.postalCode?.replace(/\D/gu, '') === digits;
    try {
      const found = await companyIdentityApi.findPostalCode(digits);
      if (!currentRequest()) return;
      const current = latest.current.value;
      onChange({
        ...current,
        address: {
          ...current.address!,
          street: found.street ?? current.address!.street,
          district: found.district ?? current.address!.district,
          city: found.city,
          state: found.state,
        },
      });
      setHint('Endereço preenchido pelo CEP. Confira e informe o número.');
    } catch (cause) {
      if (!currentRequest()) return;
      const status = (cause as { response?: { status?: number } }).response?.status;
      setHint(
        status === 404
          ? 'CEP não encontrado. Preencha o endereço à mão.'
          : 'Não foi possível consultar o CEP agora. Preencha à mão.',
      );
    }
  }
  const set = (patch: Partial<CompanyContactDetails>) => onChange({ ...value, ...patch });
  const address = value.address ?? {
    country: 'BR',
    postalCode: null,
    street: null,
    number: null,
    complement: null,
    district: null,
    city: null,
    state: null,
  };
  const names = new Intl.DisplayNames(['pt-BR'], { type: 'region' });
  return (
    <fieldset disabled={disabled} className="grid gap-4">
      <legend className="mb-2 text-sm font-semibold">Cadastro institucional da empresa</legend>
      <p className="text-sm text-content-muted">
        CNPJ, endereço e contatos próprios da empresa. As unidades mantêm seus próprios cadastros;
        copiar contatos não cria sincronização automática.
      </p>
      <div className="grid max-w-3xl gap-4 sm:grid-cols-2">
        <DocumentInput
          kind="CNPJ"
          value={value.taxId ?? ''}
          onValueChange={(taxId) => set({ taxId: taxId || null })}
        />
        <Input
          label="E-mail da empresa"
          type="email"
          value={value.email ?? ''}
          onChange={(event) => set({ email: event.target.value || null })}
        />
      </div>
      <ContactNumbersEditor
        value={value.contactNumbers}
        onChange={(contactNumbers) => set({ contactNumbers })}
        disabled={disabled}
      />
      <div className="grid max-w-3xl gap-4 sm:grid-cols-2">
        <Combobox
          label="País da empresa"
          placeholder="Buscar país"
          selectedLabel={names.of(address.country) ?? address.country}
          options={countryCodes().map((country) => ({
            value: country,
            label: names.of(country) ?? country,
          }))}
          disabled={disabled}
          onSelect={(choice) => {
            if (choice.kind === 'option')
              set({
                address: { ...address, country: choice.value, postalCode: null, state: null },
              });
          }}
        />
        {address.country === 'BR' ? (
          <MaskedInput
            label="CEP / código postal da empresa"
            inputMode="numeric"
            disabled={disabled}
            value={address.postalCode ?? ''}
            hint={hint}
            format={(digits) => digits.slice(0, 8).replace(/^(\d{5})(\d)/u, '$1-$2')}
            onValueChange={(text) => {
              set({ address: { ...address, postalCode: text || null } });
              void lookup(text);
            }}
          />
        ) : (
          <Input
            label="CEP / código postal da empresa"
            disabled={disabled}
            value={address.postalCode ?? ''}
            onChange={(event) =>
              set({ address: { ...address, postalCode: event.target.value || null } })
            }
          />
        )}
        {(['street', 'number', 'complement', 'district', 'city', 'state'] as const).map((field) => (
          <Input
            key={field}
            disabled={disabled}
            label={
              {
                street: 'Rua da empresa',
                number: 'Número da empresa',
                complement: 'Complemento da empresa',
                district: 'Bairro da empresa',
                city: 'Cidade da empresa',
                state: 'UF / estado da empresa',
              }[field]
            }
            value={address[field] ?? ''}
            onChange={(event) =>
              set({ address: { ...address, [field]: event.target.value || null } })
            }
          />
        ))}
      </div>
    </fieldset>
  );
}
